import { useCallback, useEffect, useRef, useState } from "react";
import { fetchTaskDetail } from "../../models/shared/taskDetailModel";

const isUsableTaskDetail = result => !!result
  && typeof result === "object"
  && !!result.task
  && typeof result.task === "object"
  && (result.task.id != null || result.task.task_id != null || result.task.query_pk != null || result.task.query_id != null);

const mergePresentObject = (currentValue, incomingValue) => {
  if (incomingValue === undefined) return currentValue;
  if (incomingValue === null || typeof incomingValue !== "object" || Array.isArray(incomingValue)) return incomingValue;
  return { ...(currentValue && typeof currentValue === "object" ? currentValue : {}), ...incomingValue };
};

// Task-detail refreshes can race with the transaction/socket sequence produced
// by a mutation. During that small window the endpoint may return a valid task
// identity with only the fields affected by the mutation. Replacing the whole
// snapshot with that response blanks every omitted panel. Merge only fields the
// server actually supplied; explicit empty arrays/nulls still replace old data.
export const mergeTaskDetailSnapshot = (current, incoming) => {
  if (!current) return incoming;
  const mergedTask = {
    ...current.task,
    ...incoming.task,
    customer: mergePresentObject(current.task?.customer, incoming.task?.customer),
    vehicle: mergePresentObject(current.task?.vehicle, incoming.task?.vehicle),
    assigned_user: mergePresentObject(current.task?.assigned_user, incoming.task?.assigned_user),
    customer_communication_channel: mergePresentObject(
      current.task?.customer_communication_channel,
      incoming.task?.customer_communication_channel
    ),
  };
  return {
    ...current,
    ...incoming,
    task: mergedTask,
    after_sales: mergePresentObject(current.after_sales, incoming.after_sales),
  };
};

export function useTaskDetail(queryPk) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);
  const activeQueryRef = useRef(null);

  const refetch = useCallback(async () => {
    if (!queryPk) {
      setData(null);
      return null;
    }
    const requestId = ++requestIdRef.current;
    setLoading(true);
    let result = null;
    try {
      result = await fetchTaskDetail(queryPk);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
    // fetchTaskDetail resolves null on any failure (network hiccup, transient
    // backend error) — don't let a failed background refresh wipe out
    // already-displayed, valid data. WorkflowTaskDrawer treats data === null
    // as "no task" and blanks its body, including the nested PriceUpdateDrawer
    // (its card prop derives from task, and PriceUpdateDrawer resets its own
    // parts to [] the moment card?.id goes away) — with no way to recover
    // short of closing and reopening. Keeping the stale-but-valid data instead
    // means a flaky refetch just silently retries next time instead of
    // blanking the UI.
    // Empty/malformed successful responses are just as unsafe as a rejected
    // refresh: accepting one removes data.task and blanks both the task drawer
    // and its nested Price Update drawer. Only the newest usable response may
    // replace the currently rendered canonical detail.
    if (requestId === requestIdRef.current && isUsableTaskDetail(result)) {
      setData(current => mergeTaskDetailSnapshot(current, result));
    }
    return result;
  }, [queryPk]);

  useEffect(() => {
    const normalizedQueryPk = queryPk == null ? null : String(queryPk);
    const queryChanged = activeQueryRef.current !== normalizedQueryPk;
    activeQueryRef.current = normalizedQueryPk;
    if (queryChanged) {
      requestIdRef.current += 1;
      setData(null);
    }
    if (!queryPk) {
      setLoading(false);
      return;
    }
    refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryPk]);

  return { data, loading, refetch };
}
